import { v4 as uuidV4 } from 'uuid';
import { USER_TYPES } from '@sv-support-sdk/utils/src/constants/chat';
import { MESSAGE_TYPES } from '@sv-support-sdk/utils/src/constants/chatMessages';
import { getUserAgentInfo } from '@sv-support-sdk/utils/src/helpers/deviceHelper';
import { emailValidation } from '@sv-support-sdk/utils/src/helpers/validations';
import { getNumberFromString } from '@sv-support-sdk/utils/src/helpers/string';
import { getCurrentDate } from '@sv-support-sdk/utils/src/helpers/toLocaleTime';
import { getLastMessage } from '@sv-support-sdk/utils/src/helpers/chatMessage';
import { ISupportAgent } from '@sv-support-sdk/chat-shared-types/src/common/chat';

import { TThunk } from '../index';
import { getCookie, removeCookie, setCookie } from '../../helpers/cookies';
import {
  CHAT_LAST_MESSAGE,
  CHAT_LAST_MESSAGE_READ,
  POPUP_WAS_OPENED,
  UUID_KEY,
} from '../../constants/storage';
import {
  addMessage,
  addMessages,
  deleteMessageByType,
  deleteMessageByUuid,
  setReplied,
  setUnreadMessages,
} from '../slices/messages';
import {
  setChatCompany,
  setChatData,
  setChatOpen,
  setChatSettings,
  setSupports,
  setUserBan,
} from '../slices/chat';
import { initSocket } from '../../socket';
import { closeSocketRoomConnect, initSocketConnectTo } from './socket';
import { setActiveModal, setModalData } from '../slices/modals';
import { FEEDBACK_MODAL } from '../../constants/modals';
import { CHAT_COMPANY, IS_CHAT_OPEN } from '../../constants/chat';
import { updateUser } from '../slices/user';
import { $l } from '../../locales';
import getChatCompany from '../../helpers/getChatCompany';
import { SUPPORT_ROUTE, SCREEN_SHARE_URL } from '../../config';
import { ObjectStrings } from '../../types/commonTypes';
import { ISendChatCESData } from '../../api/modules/customerEffortScore';
import { requestDemo } from '../../helpers/common';
import {
  SUPPORT_CHAT_CONTACT_SUPPORT,
  SUPPORT_CHAT_OPEN_HELP_CENTER,
  SUPPORT_CHAT_REQUEST_A_DEMO,
} from '../../constants/qaAttributes';

const { browser, os } = getUserAgentInfo();
const onSupport = () => window.location.href.includes(SUPPORT_ROUTE);

let prevChatId: number | null = null;

const getOsName = () => {
  if (!os) return '';

  return [os.name, os.version, os.versionName].filter((el) => el).join(' ');
};

export const redirectToSupport = (workspaceId: string) => () => {
  const URL = workspaceId ? `${SUPPORT_ROUTE}?workspace=${workspaceId}` : SUPPORT_ROUTE;
  window.open(URL, '_blank');
};

const fingerprint = () => {
  const onFp = getCookie('fp');

  if (onFp) return onFp;

  const newFp = uuidV4();
  setCookie('fp', newFp);
  return newFp;
};

interface ChatStartProps {
  init_message?: string;
  messageUuid?: string;
}
export const loadChat =
  (data?: ChatStartProps): TThunk =>
  async (dispatch, getState, api) => {
    const {
      user: { id, email, workspaceId },
      chat: { isMaintenance },
    } = getState();

    if (prevChatId) {
      dispatch(deleteMessageByUuid(`chat-close-${prevChatId}`));
      prevChatId = null;
    }
    const params = {
      user_id: id,
      user_name: email,
      org_id: workspaceId,
      company: getChatCompany(true),
      browser: browser ? `${browser.name} ${browser.version}` : '',
      os: getOsName(),
      uuid: getCookie(UUID_KEY) || undefined,
      fp: fingerprint(),
      referer: global.location.href,
      messageUuid: data?.messageUuid,
      welcome_message: $l('WELCOME_MESSAGE'),
      ...data,
    };

    const chat = await api.chatManagement.sendChatRequest(params);
    await initSocket();

    if (chat.success) {
      setCookie(CHAT_COMPANY, getChatCompany(true));
      dispatch(setChatCompany(getChatCompany(true)));
    }

    if (chat.uuid) {
      setCookie(UUID_KEY, chat.uuid);
      const userProps: ObjectStrings = { type: USER_TYPES.UNREGISTERED, uuid: chat.uuid };

      if (emailValidation(email) && !emailValidation(chat.userName)) {
        dispatch(updateUserName({ uuid: chat.uuid, name: email }, true));
      } else {
        userProps.email = chat.userName;
      }

      dispatch(updateUser(userProps));
    }
    dispatch(setChatData(chat));
    dispatch(sendChatLocation({ url: window.location.href }));

    const messages = chat.messages;
    if (!isMaintenance && !data?.init_message) {
      dispatch(addInitMessage(chat.createdAt));
    }
    dispatch(initSocketConnectTo());
    if (chat.initMessage) {
      messages.push(chat.initMessage);
    }
    const agentsObject = messages.reduce((acc: { [key: string]: ISupportAgent }, message) => {
      if (message.authorType !== 'staff' || acc[message.authorableId]) return acc;

      acc[message.authorableId] = {
        id: message.authorableId,
        name: message.authorName,
        img: message.img,
      };

      return acc;
    }, {});

    dispatch(setSupports(Object.values(agentsObject)));
    dispatch(addMessages(messages));
    const {
      chat: { isOpen },
    } = getState();
    if (isOpen) return;

    const lastMessageFromStaff = getLastMessage(messages, true);
    const lastSaved = getCookie(CHAT_LAST_MESSAGE);

    dispatch(setUnreadMessages(!!lastSaved && lastMessageFromStaff > +lastSaved));
  };

export const checkChat =
  (): TThunk<Promise<number | boolean>> => async (dispatch, getState, api) => {
    const {
      user: { id, email },
    } = getState();
    const uuid = getCookie(UUID_KEY);

    const data = {
      user_id: id,
      user_name: email,
      uuid: uuid || undefined,
      company: getChatCompany(true),
    };

    if (!id && !emailValidation(email) && !uuid) {
      dispatch(clearChatData());
      return Promise.resolve(false);
    }

    try {
      const { status } = await api.chatStatus.checkChat(data);

      if (!status) {
        dispatch(clearChatData());
      }

      return status;
    } catch (e) {
      return false;
    }
  };

const addInitMessage =
  (messageDate?: string): TThunk =>
  (dispatch, getState) => {
    const {
      user: { workspaceId },
    } = getState();

    dispatch(
      addMessage({
        type: MESSAGE_TYPES.DIVIDER,
        message: $l('NEW_CONVERSATION'),
        id: uuidV4(),
      }),
    );
    dispatch(
      addMessage({
        type: MESSAGE_TYPES.MESSAGE,
        id: uuidV4(),
        message: $l('HELLO_MESSAGE'),
        createdAt: messageDate || getCurrentDate(),
      }),
    );

    const actions = [];

    if (!onSupport()) {
      actions.push({
        name: $l('OPEN_HELP_CENTER'),
        method: redirectToSupport(workspaceId),
        qaId: SUPPORT_CHAT_OPEN_HELP_CENTER,
      });
    }

    actions.push({
      name: $l('REQUEST_A_DEMO'),
      method: requestDemo,
      qaId: SUPPORT_CHAT_REQUEST_A_DEMO,
    });

    dispatch(
      addMessage({
        type: MESSAGE_TYPES.QUICK_ACTIONS,
        actions,
        uuid: uuidV4(),
      }),
    );
  };
export const initChat = (): TThunk => async (dispatch, getState) => {
  const {
    user: { id },
  } = getState();
  const status = await dispatch(checkChat());
  const isChatOpened = getCookie(IS_CHAT_OPEN);
  if (isChatOpened) {
    dispatch(setChatOpen(true));
  }

  await dispatch(checkForBan());

  try {
    if (status) {
      dispatch(loadChat());
      dispatch(setChatSettings({ isOnline: true }));
    } else {
      await dispatch(checkChatSettings());
      const {
        chat: { isMaintenance, isOnline },
      } = getState();
      if (!isMaintenance && isOnline) {
        dispatch(addInitMessage());
      }

      const popupWasClosed = getCookie(POPUP_WAS_OPENED);
      const numberFromId = getNumberFromString(id);

      if (popupWasClosed !== numberFromId) {
        removeCookie(POPUP_WAS_OPENED);
      }
    }
  } catch (e) {
    /* empty */
  }
};

const maintenanceOpenChat = (): TThunk => async (dispatch) => {
  dispatch(
    addMessage({ type: MESSAGE_TYPES.MESSAGE, message: $l('WE_CONNECT_YOU'), uuid: uuidV4() }),
  );
  await dispatch(loadChat());
  dispatch(setChatSettings({ isMaintenance: false, isOnline: true }));
  dispatch(deleteMessageByType('chatActions'));
};

export const checkChatSettings = (): TThunk => async (dispatch, getState, api) => {
  const {
    user: { workspaceId },
  } = getState();
  const { data } = await api.chatStatus.checkChatSettings();
  try {
    dispatch(setChatSettings(data));
    if (!data.isOnline) {
      dispatch(
        addMessage({
          type: MESSAGE_TYPES.TICKET,
        }),
      );
    } else if (data.isMaintenance) {
      if (data.maintenanceMessage) {
        dispatch(
          addMessage({
            type: MESSAGE_TYPES.MESSAGE,
            uuid: uuidV4(),
            message: data.maintenanceMessage,
          }),
        );

        const actions = [
          {
            name: $l('CONTACT_SUPPORT'),
            method: () => {
              dispatch(maintenanceOpenChat());
            },
            qaId: SUPPORT_CHAT_CONTACT_SUPPORT,
          },
        ];

        if (!onSupport()) {
          actions.push({
            name: $l('OPEN_HELP_CENTER'),
            method: redirectToSupport(workspaceId),
            qaId: SUPPORT_CHAT_OPEN_HELP_CENTER,
          });
        }

        actions.push({
          name: $l('REQUEST_A_DEMO'),
          method: requestDemo,
          qaId: SUPPORT_CHAT_REQUEST_A_DEMO,
        });

        dispatch(
          addMessage({
            type: MESSAGE_TYPES.QUICK_ACTIONS,
            actions,
            uuid: uuidV4(),
          }),
        );
      }
    }
  } catch (e) {
    dispatch(addMessage({ type: MESSAGE_TYPES.TICKET }));
  }
};

export const sendFeedback =
  (chatId: number, data: ISendChatCESData, fromComment?: boolean): TThunk =>
  async (dispatch, getState, api) => {
    const {
      user: { workspaceId },
      initData,
    } = getState();

    await api.customerEffortScore.sendChatCES(chatId, {
      ...data,
      organization_id: workspaceId || undefined,
    });

    initData?.onChatClose?.();

    if (fromComment) {
      dispatch(setActiveModal(FEEDBACK_MODAL));
      dispatch(setModalData({ chatId: chatId, score: data.score, hash: data.hash }));
    } else {
      dispatch(setActiveModal(''));
    }
  };

export const closeChat = (): TThunk => async (dispatch, getState, api) => {
  const { user, chat, initData } = getState();

  dispatch(updateUser({ emailAsked: false }));
  dispatch(setReplied(false));

  if (!chat.id) return;
  prevChatId = chat.id;

  const closeResult = await api.chatManagement.closeChat({
    user_id: user.id,
    chat_id: chat.id,
    hash: chat.hash,
  });

  initData?.onChatClose?.();

  if (closeResult.withCsat && chat.connectedSupports.length) {
    dispatch(
      addMessage({
        type: MESSAGE_TYPES.FEEDBACK,
        chatId: chat.id,
        hash: chat.hash,
        uuid: `chat-close-${chat.id}`,
      }),
    );
  }

  dispatch(setSupports([]));
  dispatch(
    setChatData({
      hash: '',
      id: '',
      room: '',
    }),
  );

  dispatch(clearChatData());
  dispatch(checkChatSettings());
  dispatch(closeSocketRoomConnect());

  if (chat.id) {
    dispatch(
      addMessage({
        type: MESSAGE_TYPES.CHAT_IS_CLOSED,
        uuid: uuidV4(),
      }),
    );
  }
};

const clearChatData = (): TThunk => async (dispatch) => {
  dispatch(updateUser({ uuid: '' }));
  removeCookie(UUID_KEY);
  removeCookie(CHAT_LAST_MESSAGE);
  removeCookie(CHAT_LAST_MESSAGE_READ);
  dispatch(updateUser({ uuid: '' }));
};

export const checkForBan = (): TThunk => async (dispatch, getState, api) => {
  try {
    const {
      user: { email },
    } = getState();
    const dataToCheckBan: { email?: string } = {};

    if (email && email !== 'Customer' && emailValidation(email)) {
      dataToCheckBan.email = email;
    }

    const { ban } = await api.chatStatus.checkForBan(dataToCheckBan);

    dispatch(setUserBan(ban));

    return ban;
  } catch (e) {
    return false;
  }
};

export const updateUserName =
  (data: { uuid: string; name: string }, withoutNotification = false): TThunk =>
  async (dispatch, getState, api) => {
    try {
      await api.userProfile.updateUserEmail(data);
      dispatch(updateUser({ email: data.name }));
      if (!withoutNotification) {
        dispatch(deleteMessageByType(MESSAGE_TYPES.ASK_EMAIL));
        dispatch(
          addMessage({
            type: MESSAGE_TYPES.NOTIFICATION,
            uuid: uuidV4(),
            title: 'Thank you!',
          }),
        );
      }
    } catch (e) {
      /* empty */
    }
  };

export const startStream = (): TThunk => async (dispatch, getState, api) => {
  const {
    chat: { id, hash, room },
  } = getState();

  if (!id || !hash || !room) return;

  try {
    const { data } = await api.screenSharing.screenShareJoinClient({ id, hash });

    if (data) {
      const channel = data.channel.match(/room.(.*)/g);

      if (channel) {
        window.open(
          `${SCREEN_SHARE_URL}/?room=${channel[0]}&product=workflow&chat=${room}&id=${data.token}&socketClient=pusher&lang=en`,
          '_blank',
        );
      }
    }
  } catch (e) {
    /* empty */
  }
};

export const setChatOpenState =
  (isOpen: boolean): TThunk =>
  async (dispatch) => {
    if (isOpen) {
      setCookie(IS_CHAT_OPEN, 1);
    } else {
      removeCookie(IS_CHAT_OPEN);
    }

    dispatch(setChatOpen(isOpen));
  };

export const sendChatLocation =
  (data: { url: string }): TThunk =>
  async (_, getState, api) => {
    const {
      chat: { id, hash },
    } = getState();

    if (id && hash) {
      await api.chatManagement.sendChatLocation(id, { ...data, hash });
    }
  };
